// VIP World Level Configurations (V1-V50)
// Exclusive world for Premium Members with 12x12 grid levels
// Features: Larger board, VIP items, unique mechanics, epic rewards

// VIP World Theme: Royal Citadel
// A grand royal fortress with premium-exclusive 12x12 challenges
// Enhanced rewards, VIP items, exclusive cosmetics

export const VIP_WORLD_CONFIG = {
  id: "vip_world",
  name: "Royal Citadel",
  subtitle: "VIP Elite",
  emoji: "👑",
  color: "#fbbf24",
  unlockRequirement: "premium_membership",
  levelRange: [8001, 8050], // Special range for VIP world
  benefits: {
    coinMultiplier: 3.0,
    xpMultiplier: 1.5,
    noLifeCost: true,
    vipItemDrops: true,
    bonusRewards: true
  },
  boardSize: 12 // Larger 12x12 grid!
};

// VIP Item definitions - exclusive rewards only from VIP world
export const VIP_ITEMS = [
  {
    id: "royal_scepter",
    name: "Royal Scepter",
    emoji: "👑",
    rarity: "legendary",
    description: "Clears entire column and row in a cross pattern",
    effect: "cross_clear",
    dropChance: 0.05
  },
  {
    id: "golden_crown",
    name: "Golden Crown",
    emoji: "🎗️",
    rarity: "legendary",
    description: "+100% score for 3 turns",
    effect: "score_boost",
    duration: 3,
    dropChance: 0.05
  },
  {
    id: "diamond_heart",
    name: "Diamond Heart",
    emoji: "💎",
    rarity: "epic",
    description: "Restores full HP instantly",
    effect: "full_heal",
    dropChance: 0.08
  },
  {
    id: "ruby_ring",
    name: "Ruby Ring",
    emoji: "💍",
    rarity: "epic",
    description: "+5 extra moves",
    effect: "extra_moves",
    moveBonus: 5,
    dropChance: 0.10
  },
  {
    id: "sapphire_orb",
    name: "Sapphire Orb",
    emoji: "🔮",
    rarity: "epic",
    description: "Shuffles board optimally",
    effect: "optimal_shuffle",
    dropChance: 0.10
  },
  {
    id: "emerald_chalice",
    name: "Emerald Chalice",
    emoji: "🏆",
    rarity: "epic",
    description: "2x coins for this level",
    effect: "double_coins",
    dropChance: 0.12
  },
  {
    id: "amethyst_amulet",
    name: "Amethyst Amulet",
    emoji: "📿",
    rarity: "rare",
    description: "Highlights all possible matches for 30s",
    effect: "match_highlight",
    duration: 30,
    dropChance: 0.15
  },
  {
    id: "topaz_token",
    name: "Topaz Token",
    emoji: "🪙",
    rarity: "rare",
    description: "+500 bonus coins",
    effect: "bonus_coins",
    coinBonus: 500,
    dropChance: 0.15
  },
  {
    id: "pearl_pendant",
    name: "Pearl Pendant",
    emoji: "📿",
    rarity: "rare",
    description: "Removes all obstacles of one type",
    effect: "obstacle_clear",
    dropChance: 0.12
  },
  {
    id: "opal_orb",
    name: "Opal Orb",
    emoji: "🌙",
    rarity: "rare",
    description: "Creates 3 random special fruits",
    effect: "spawn_specials",
    specialCount: 3,
    dropChance: 0.15
  }
];

// VIP-exclusive cosmetics unlocked from completing VIP levels
export const VIP_COSMETICS = {
  skins: [
    { id: "vip_royal", name: "Royal Fruits", unlockLevel: 8010, emoji: "👑" },
    { id: "vip_golden", name: "24K Gold Fruits", unlockLevel: 8025, emoji: "✨" },
    { id: "vip_diamond", name: "Diamond Encrusted", unlockLevel: 8040, emoji: "💎" },
    { id: "vip_platinum", name: "Platinum Elite", unlockLevel: 8050, emoji: "🏆" }
  ],
  frames: [
    { id: "vip_crown", name: "Royal Crown Frame", unlockLevel: 8015, emoji: "👑" },
    { id: "vip_jeweled", name: "Jeweled Frame", unlockLevel: 8030, emoji: "💍" },
    { id: "vip_legendary", name: "Legendary Frame", unlockLevel: 8045, emoji: "⭐" }
  ],
  effects: [
    { id: "vip_sparkle", name: "Royal Sparkles", unlockLevel: 8020, emoji: "✨" },
    { id: "vip_aura", name: "Golden Aura", unlockLevel: 8035, emoji: "🌟" }
  ]
};

// Generate 50 VIP world levels with 12x12 grid
export const VIP_WORLD_LEVELS = [
  // Levels V1-V10: Royal Gardens (Introduction to 12x12)
  {
    id: 8001,
    name: "Royal Garden I",
    subtitle: "Welcome, VIP!",
    difficulty: "Hard",
    missionType: "collect_fruit",
    targetFruit: "grape",
    targetEmoji: "🍇",
    targetCount: 60,
    moves: 45,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.08,
    obstacles: { type: "royal", golden_hedge: 6 },
    bgColor: "from-yellow-900 via-amber-800 to-orange-700",
    borderColor: "border-yellow-400",
    vipExclusive: true,
    rewards: { coins: 500, xp: 400, vipItem: "topaz_token" }
  },
  {
    id: 8002,
    name: "Royal Garden II",
    subtitle: "Golden Path",
    difficulty: "Hard",
    missionType: "reach_score",
    targetScore: 20000,
    moves: 40,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.08,
    obstacles: { type: "royal", golden_hedge: 8 },
    bgColor: "from-amber-900 via-yellow-800 to-orange-700",
    borderColor: "border-amber-400",
    vipExclusive: true,
    rewards: { coins: 550, xp: 425 }
  },
  {
    id: 8003,
    name: "Royal Garden III",
    subtitle: "Crown Courtyard",
    difficulty: "Hard",
    missionType: "collect_fruit",
    targetFruit: "cherry",
    targetEmoji: "🍒",
    targetCount: 70,
    moves: 42,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.10,
    obstacles: { type: "royal", crown_block: 8 },
    bgColor: "from-red-900 via-rose-800 to-pink-700",
    borderColor: "border-red-400",
    vipExclusive: true,
    rewards: { coins: 600, xp: 450 }
  },
  {
    id: 8004,
    name: "Royal Garden IV",
    subtitle: "Emerald Terrace",
    difficulty: "Hard",
    missionType: "clear_blockers",
    blockerType: "golden_hedge",
    targetCount: 20,
    moves: 38,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.10,
    obstacles: { type: "royal", golden_hedge: 20, emerald_pillar: 4 },
    bgColor: "from-green-900 via-emerald-800 to-teal-700",
    borderColor: "border-emerald-400",
    vipExclusive: true,
    rewards: { coins: 650, xp: 475, vipItem: "emerald_chalice" }
  },
  {
    id: 8005,
    name: "Royal Garden V",
    subtitle: "Sapphire Fountain",
    difficulty: "Hard",
    missionType: "reach_score",
    targetScore: 25000,
    moves: 40,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.12,
    obstacles: { type: "royal", sapphire_fountain: 4, golden_hedge: 10 },
    bgColor: "from-blue-900 via-indigo-800 to-violet-700",
    borderColor: "border-blue-400",
    vipExclusive: true,
    rewards: { coins: 700, xp: 500 }
  },
  {
    id: 8006,
    name: "Royal Garden VI",
    subtitle: "Ruby Pavilion",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "strawberry",
    targetEmoji: "🍓",
    targetCount: 80,
    moves: 42,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.12,
    obstacles: { type: "royal", ruby_pillar: 6, golden_hedge: 8 },
    bgColor: "from-rose-900 via-red-800 to-orange-700",
    borderColor: "border-rose-400",
    vipExclusive: true,
    rewards: { coins: 750, xp: 525, vipItem: "ruby_ring" }
  },
  {
    id: 8007,
    name: "Royal Garden VII",
    subtitle: "Diamond Gazebo",
    difficulty: "Expert",
    missionType: "reach_score",
    targetScore: 30000,
    moves: 38,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.14,
    obstacles: { type: "royal", diamond_gazebo: 4, golden_hedge: 12 },
    bgColor: "from-cyan-900 via-sky-800 to-blue-700",
    borderColor: "border-cyan-400",
    vipExclusive: true,
    rewards: { coins: 800, xp: 550 }
  },
  {
    id: 8008,
    name: "Royal Garden VIII",
    subtitle: "Pearl Pond",
    difficulty: "Expert",
    missionType: "collect_fruit",
    targetFruit: "blueberry",
    targetEmoji: "🫐",
    targetCount: 85,
    moves: 40,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.14,
    obstacles: { type: "royal", pearl_pond: 6, golden_hedge: 10 },
    bgColor: "from-slate-800 via-gray-700 to-zinc-600",
    borderColor: "border-slate-400",
    vipExclusive: true,
    rewards: { coins: 850, xp: 575, vipItem: "pearl_pendant" }
  },
  {
    id: 8009,
    name: "Royal Garden IX",
    subtitle: "Opal Orchard",
    difficulty: "Expert",
    missionType: "clear_blockers",
    blockerType: "crown_block",
    targetCount: 25,
    moves: 38,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.15,
    obstacles: { type: "royal", crown_block: 25, opal_tree: 4 },
    bgColor: "from-purple-900 via-violet-800 to-fuchsia-700",
    borderColor: "border-purple-400",
    vipExclusive: true,
    rewards: { coins: 900, xp: 600 }
  },
  {
    id: 8010,
    name: "Royal Guardian",
    subtitle: "First VIP Boss",
    difficulty: "Boss",
    missionType: "boss_fight",
    bossConfig: {
      name: "Royal Guardian",
      type: "royal_guardian",
      hp: 1500,
      phases: 3,
      attacks: ["crown_smash", "royal_decree", "golden_barrage"]
    },
    moves: 50,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.20,
    bgColor: "from-yellow-600 via-amber-500 to-orange-400",
    borderColor: "border-yellow-300",
    vipExclusive: true,
    rewards: { coins: 2000, xp: 1000, stars: 50, vipItem: "diamond_heart" },
    cosmeticUnlock: { type: "skins", id: "vip_royal" }
  },
  
  // Levels V11-V20: Throne Hall
  ...generateThroneHallLevels(),
  
  // Levels V21-V35: Royal Treasury
  ...generateRoyalTreasuryLevels(),
  
  // Levels V36-V49: Crown Sanctum
  ...generateCrownSanctumLevels(),
  
  // Level V50: Final Boss - The Emperor
  {
    id: 8050,
    name: "The Emperor",
    subtitle: "Ultimate VIP Challenge",
    difficulty: "Legendary",
    missionType: "boss_fight",
    bossConfig: {
      name: "The Emperor",
      type: "the_emperor",
      hp: 5000,
      phases: 6,
      attacks: ["imperial_wrath", "crown_of_thorns", "golden_storm", "royal_devastation", "emperor_judgment", "divine_coronation"]
    },
    moves: 75,
    boardSize: 12,
    worldMechanic: "vip_drops",
    vipDropChance: 0.35,
    bgColor: "from-yellow-400 via-amber-300 to-orange-200",
    borderColor: "border-yellow-200",
    vipExclusive: true,
    rewards: { coins: 10000, xp: 5000, stars: 500, vipItem: "royal_scepter" },
    cosmeticUnlock: { type: "skins", id: "vip_platinum" }
  }
];

// Helper function to generate Throne Hall levels (V11-V20)
function generateThroneHallLevels() {
  const levels = [];
  const subtitles = ["Grand Entrance", "Marble Steps", "Gilded Columns", "Velvet Carpet", "Royal Banner", "Crystal Chandelier", "Ornate Ceiling", "Ancient Tapestry", "Noble Statues", "Throne Ascent"];
  
  for (let i = 0; i < 10; i++) {
    const levelId = 8011 + i;
    const isLastBoss = i === 9;
    
    if (isLastBoss) {
      levels.push({
        id: levelId,
        name: "Throne Keeper",
        subtitle: "Second VIP Boss",
        difficulty: "Boss",
        missionType: "boss_fight",
        bossConfig: {
          name: "Throne Keeper",
          type: "throne_keeper",
          hp: 2200,
          phases: 4,
          attacks: ["throne_slam", "scepter_beam", "royal_summon", "crown_nova"]
        },
        moves: 55,
        boardSize: 12,
        worldMechanic: "vip_drops",
        vipDropChance: 0.22,
        bgColor: "from-purple-600 via-violet-500 to-fuchsia-400",
        borderColor: "border-purple-300",
        vipExclusive: true,
        rewards: { coins: 3000, xp: 1500, stars: 75, vipItem: "golden_crown" },
        cosmeticUnlock: { type: "frames", id: "vip_crown" }
      });
    } else {
      const missionType = i % 3;
      levels.push({
        id: levelId,
        name: `Throne Hall ${['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'][i]}`,
        subtitle: subtitles[i],
        difficulty: i < 5 ? "Expert" : "Master",
        missionType: missionType === 0 ? "reach_score" : missionType === 1 ? "collect_fruit" : "clear_blockers",
        targetScore: missionType === 0 ? 35000 + (i * 3000) : undefined,
        targetFruit: missionType === 1 ? ["orange", "apple", "banana", "mango", "watermelon", "peach", "plum", "kiwi", "coconut"][i] : undefined,
        targetEmoji: missionType === 1 ? ["🍊", "🍎", "🍌", "🥭", "🍉", "🍑", "🫐", "🥝", "🥥"][i] : undefined,
        targetCount: missionType === 1 ? 90 + (i * 5) : missionType === 2 ? 25 + (i * 2) : undefined,
        blockerType: missionType === 2 ? "throne_block" : undefined,
        moves: 42 - Math.floor(i / 3),
        boardSize: 12,
        worldMechanic: "vip_drops",
        vipDropChance: 0.15 + (i * 0.01),
        obstacles: { type: "throne", throne_block: 10 + (i * 2), royal_guard: Math.floor(i / 2) },
        bgColor: "from-indigo-900 via-purple-800 to-violet-700",
        borderColor: "border-indigo-400",
        vipExclusive: true,
        rewards: { 
          coins: 1000 + (i * 100), 
          xp: 650 + (i * 50),
          vipItem: i === 4 ? "sapphire_orb" : undefined
        }
      });
    }
  }
  
  return levels;
}

// Helper function to generate Royal Treasury levels (V21-V35)
function generateRoyalTreasuryLevels() {
  const levels = [];
  const subtitles = ["Gold Vault", "Silver Chamber", "Gem Repository", "Coin Cascade", "Jewel Room", "Artifact Hall", "Crown Jewels", "Treasure Trove", "Diamond Cache", "Ruby Reserve", "Sapphire Stores", "Emerald Alcove", "Pearl Parlor", "Platinum Pit", "Treasury Master"];
  
  for (let i = 0; i < 15; i++) {
    const levelId = 8021 + i;
    const isLastBoss = i === 14;
    
    if (isLastBoss) {
      levels.push({
        id: levelId,
        name: "Treasury Dragon",
        subtitle: "Third VIP Boss",
        difficulty: "Boss",
        missionType: "boss_fight",
        bossConfig: {
          name: "Treasury Dragon",
          type: "treasury_dragon",
          hp: 3200,
          phases: 5,
          attacks: ["gold_breath", "coin_storm", "gem_barrage", "treasure_avalanche", "hoard_fury"]
        },
        moves: 60,
        boardSize: 12,
        worldMechanic: "vip_drops",
        vipDropChance: 0.25,
        bgColor: "from-yellow-500 via-amber-400 to-orange-300",
        borderColor: "border-yellow-200",
        vipExclusive: true,
        rewards: { coins: 5000, xp: 2500, stars: 100, vipItem: "royal_scepter" },
        cosmeticUnlock: { type: "frames", id: "vip_jeweled" }
      });
    } else {
      const missionType = i % 3;
      levels.push({
        id: levelId,
        name: `Royal Treasury ${['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV'][i]}`,
        subtitle: subtitles[i],
        difficulty: i < 7 ? "Master" : "Legendary",
        missionType: missionType === 0 ? "reach_score" : missionType === 1 ? "collect_fruit" : "clear_blockers",
        targetScore: missionType === 0 ? 50000 + (i * 4000) : undefined,
        targetFruit: missionType === 1 ? ["grape", "cherry", "orange", "apple", "banana", "strawberry", "blueberry", "watermelon", "mango", "pear", "peach", "plum", "kiwi", "coconut"][i] : undefined,
        targetEmoji: missionType === 1 ? ["🍇", "🍒", "🍊", "🍎", "🍌", "🍓", "🫐", "🍉", "🥭", "🍐", "🍑", "🫐", "🥝", "🥥"][i] : undefined,
        targetCount: missionType === 1 ? 100 + (i * 8) : missionType === 2 ? 30 + (i * 3) : undefined,
        blockerType: missionType === 2 ? "treasure_chest" : undefined,
        moves: 45 - Math.floor(i / 4),
        boardSize: 12,
        worldMechanic: "treasure_bonus",
        treasureBonus: 1.5 + (i * 0.1),
        obstacles: { type: "treasury", treasure_chest: 12 + i, gold_pile: Math.floor(i / 2), gem_cluster: Math.floor(i / 3) },
        bgColor: "from-amber-800 via-yellow-700 to-orange-600",
        borderColor: "border-amber-300",
        vipExclusive: true,
        rewards: { 
          coins: 1500 + (i * 150), 
          xp: 900 + (i * 75),
          vipItem: [undefined, undefined, "amethyst_amulet", undefined, undefined, "opal_orb", undefined, undefined, undefined, "diamond_heart", undefined, undefined, undefined, undefined][i]
        }
      });
    }
  }
  
  return levels;
}

// Helper function to generate Crown Sanctum levels (V36-V49)
function generateCrownSanctumLevels() {
  const levels = [];
  const subtitles = ["Inner Sanctum", "Crown Chamber", "Divine Altar", "Celestial Path", "Sacred Hall", "Eternal Flame", "Royal Blessing", "Crown's Rest", "Sanctified Ground", "Heaven's Gate", "Divine Throne", "Coronation Hall", "Ultimate Trial", "Emperor's Door"];
  
  for (let i = 0; i < 14; i++) {
    const levelId = 8036 + i;
    const isLastBoss = i === 13;
    
    if (isLastBoss) {
      levels.push({
        id: levelId,
        name: "Crown Avatar",
        subtitle: "Fourth VIP Boss",
        difficulty: "Boss",
        missionType: "boss_fight",
        bossConfig: {
          name: "Crown Avatar",
          type: "crown_avatar",
          hp: 4000,
          phases: 5,
          attacks: ["divine_light", "crown_beam", "royal_wrath", "sanctum_storm", "avatar_judgment"]
        },
        moves: 65,
        boardSize: 12,
        worldMechanic: "vip_drops",
        vipDropChance: 0.30,
        bgColor: "from-violet-500 via-purple-400 to-fuchsia-300",
        borderColor: "border-violet-200",
        vipExclusive: true,
        rewards: { coins: 7500, xp: 3500, stars: 150, vipItem: "golden_crown" },
        cosmeticUnlock: { type: "frames", id: "vip_legendary" }
      });
    } else {
      const missionType = i % 3;
      levels.push({
        id: levelId,
        name: `Crown Sanctum ${['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII'][i]}`,
        subtitle: subtitles[i],
        difficulty: "Legendary",
        missionType: missionType === 0 ? "reach_score" : missionType === 1 ? "collect_fruit" : "clear_blockers",
        targetScore: missionType === 0 ? 75000 + (i * 5000) : undefined,
        targetFruit: missionType === 1 ? ["grape", "cherry", "orange", "apple", "banana", "strawberry", "blueberry", "watermelon", "mango", "pear", "peach", "plum", "kiwi"][i] : undefined,
        targetEmoji: missionType === 1 ? ["🍇", "🍒", "🍊", "🍎", "🍌", "🍓", "🫐", "🍉", "🥭", "🍐", "🍑", "🫐", "🥝"][i] : undefined,
        targetCount: missionType === 1 ? 120 + (i * 10) : missionType === 2 ? 40 + (i * 4) : undefined,
        blockerType: missionType === 2 ? "sanctum_seal" : undefined,
        moves: 50 - Math.floor(i / 3),
        boardSize: 12,
        worldMechanic: "divine_power",
        divinePowerChance: 0.15 + (i * 0.015),
        obstacles: { type: "sanctum", sanctum_seal: 15 + (i * 2), divine_barrier: Math.floor(i / 2), crown_relic: Math.floor(i / 3) },
        bgColor: "from-purple-950 via-violet-900 to-indigo-800",
        borderColor: "border-purple-400",
        vipExclusive: true,
        rewards: { 
          coins: 2500 + (i * 200), 
          xp: 1200 + (i * 100),
          vipItem: [undefined, "ruby_ring", undefined, undefined, "sapphire_orb", undefined, undefined, "diamond_heart", undefined, undefined, "golden_crown", undefined, undefined][i]
        }
      });
    }
  }
  
  return levels;
}

// Export helper to check if a level is VIP-exclusive
export function isVIPLevel(levelId) {
  return levelId >= 8001 && levelId <= 8050;
}

// Export helper to get VIP world level by ID
export function getVIPLevelById(levelId) {
  return VIP_WORLD_LEVELS.find(level => level.id === levelId);
}

// Get random VIP item drop based on chances
export function getRandomVIPItemDrop() {
  const roll = Math.random();
  let cumulative = 0;
  
  for (const item of VIP_ITEMS) {
    cumulative += item.dropChance;
    if (roll < cumulative) {
      return item;
    }
  }
  
  return null;
}

// Check if player has unlocked a VIP cosmetic
export function checkVIPCosmeticUnlock(levelId, completedLevels) {
  const allCosmetics = [
    ...VIP_COSMETICS.skins,
    ...VIP_COSMETICS.frames,
    ...VIP_COSMETICS.effects
  ];
  
  const unlockedCosmetic = allCosmetics.find(c => c.unlockLevel === levelId);
  if (unlockedCosmetic && completedLevels.includes(levelId)) {
    return unlockedCosmetic;
  }
  
  return null;
}
